import React from 'react'
import * as H from 'history'

import { PageAdditionalProps } from 'page-lifecycle-provider'
import { Logger } from 'typescript-log'

import { ArticlePublication, GalleryPublication } from 'common/client-data-types/publication'

import { renderPublicationSkeleton } from 'component-library/templates/Publication/lib/render-publication-skeleton'

import {
    RemainingPublicationInformation,
    GalleryPublicationInformation,
    GalleryPublicationProps,
} from 'component-library/templates/Publication/SharedPublication.routing'
import { getPublicationPageProperties } from 'component-library/templates/Publication/lib/publication-page-properties'
import { AdState } from 'common/advertising/ad-state'
import { TogglesState } from 'common/feature-togglings/reducer'
import { Topic } from 'common/client-data-types/topic'
import { GetVideoQueue } from 'component-library/content/Video/PlayerInterface'
import {
    RelatedPublications,
    PublicationProps,
} from 'component-library/templates/Publication/Publication.props'
import { getInlinePositionedContent } from './get-inline-positioned-content'
import { AllEvents } from 'common/events/all-events'

export type PublicationTypes = ArticlePublication | GalleryPublication | undefined
export type MetaTypes = GalleryPublicationInformation | RemainingPublicationInformation

export const maxSecondLevel = (topic: Topic) => {
    while (topic.parent && topic.parent.parent) {
        topic = topic.parent
    }

    return topic
}

export interface RenderPublicationOptions {
    publication: PublicationTypes
    publicationComponent: React.ComponentType<PublicationProps>
    galleryComponent: React.ComponentType<GalleryPublicationProps>
    meta: MetaTypes
    publicUrl: string
    adState: AdState
    onEvent: (event: AllEvents) => void
    log: Logger
    location: H.Location
    getVideoQueue: GetVideoQueue
    relatedPublications: RelatedPublications | undefined
    userTopicPublications?: RelatedPublications
    toggles: TogglesState
    isVideoStickyEnabled?: boolean
}

export const renderPublication = (options: RenderPublicationOptions) => {
    const {
        publication,
        publicationComponent: Publication,
        galleryComponent: Gallery,
        meta,
        adState,
        onEvent,
        log,
        location,
        getVideoQueue,
        relatedPublications,
        userTopicPublications,
        toggles,
        isVideoStickyEnabled,
    } = options

    if (!publication) {
        return renderPublicationSkeleton(meta, adState, onEvent)
    }

    // Todo: This should get moved into the product publication component because it gives us better access to
    // individual product configs
    const inlinePositionedContent = getInlinePositionedContent({
        adState,
        publication,
        meta,
        log,
        onEvent,
        toggles,
    })

    if (publication.kind === 'gallery') {
        const galleryMeta = meta as GalleryPublicationInformation
        return (
            <PageAdditionalProps pageProperties={getPublicationPageProperties(publication)}>
                <Gallery
                    adState={adState}
                    gallery={publication}
                    galleryMeta={galleryMeta}
                    location={location}
                    onEvent={onEvent}
                />
            </PageAdditionalProps>
        )
    } else {
        return (
            <PageAdditionalProps pageProperties={getPublicationPageProperties(publication)}>
                <Publication
                    data={publication}
                    inlinePositionedContent={inlinePositionedContent}
                    sectionAssignment={{
                        text: publication.primaryTopic.title,
                        topic: maxSecondLevel(publication.primaryTopic),
                        sizing: 'large',
                    }}
                    onEvent={onEvent}
                    log={log}
                    getVideoQueue={getVideoQueue}
                    heroOptions={{
                        isVideoStickyEnabled,
                    }}
                    relatedPublications={relatedPublications}
                    userTopicPublications={userTopicPublications}
                    location={location}
                    adUnitPath={adState.targeting.adUnitPath}
                />
            </PageAdditionalProps>
        )
    }
}
